/**
 * Coordinate parsing for the gate inputs.
 *
 * parseLatLon() accepts what people actually paste:
 *   "58.38753, 26.73208"          Google Maps pin copy
 *   "58.38753 26.73208"           space separated
 *   "58.38753; 26.73208"          semicolon separated
 *   "58.38753N, 26.73208E"        hemisphere letters (either side; fixes order)
 *   "58°22'55.1"N 26°43'55.5"E"   DMS — the Google Maps place-card format
 *   "https://…/maps/@58.38753,26.73208,14z"      maps URL (viewport centre)
 *   "https://…openstreetmap.org/#map=15/58.4/26.7"  OSM URL (zoom/lat/lon)
 *
 * Anything else is REJECTED (null) rather than guessed at: the first
 * shipped version took "the first two numbers anywhere" and silently
 * placed gates hundreds of km off for DMS pastes (degrees+minutes read
 * as lat/lon), OSM URLs (zoom read as latitude) and decimal-comma
 * pairs. Exactly two coordinate tokens or a recognized URL shape, or
 * it's not a pair.
 *
 * Returns null when no plausible pair is found or values are out of
 * range (|lat| ≤ 90, |lon| ≤ 180) — callers treat null as "don't
 * touch the committed gate".
 */

export interface LatLon {
  lat: number;
  lon: number;
}

interface Token {
  value: number;
  hemi: string | null;
}

const TOKEN_RE = /([NSEW])?\s*([+-]?\d{1,3}(?:\.\d+)?)\s*°?\s*([NSEW])?/gi;

/** Strict whole-string grammar for plain-text pastes: exactly two
 *  coordinate tokens separated by comma/semicolon/whitespace, nothing
 *  else. A scan-and-count approach still passed embedded numbers
 *  ("v1.2.3" → tokens "1.2","3"); full-match rejects any residue. */
const TOKEN_SRC = String.raw`([NSEW])?\s*([+-]?\d{1,3}(?:\.\d+)?)\s*°?\s*([NSEW])?`;
const PAIR_RE = new RegExp(`^${TOKEN_SRC}(?:\\s*[,;]\\s*|\\s+)${TOKEN_SRC}$`, 'i');

/** D°M'S"H — hemisphere letter on either side, seconds optional,
 *  typographic prime/quote variants accepted. */
const DMS_RE =
  /([NSEW])?\s*(\d{1,3})\s*°\s*([0-5]?\d)\s*['′’]\s*(?:([0-5]?\d(?:\.\d+)?)\s*["″”])?\s*([NSEW])?/gi;

const OSM_RE = /#map=\d{1,2}\/([+-]?\d{1,3}(?:\.\d+)?)\/([+-]?\d{1,3}(?:\.\d+)?)/i;

/** Order a token pair into lat/lon: hemisphere letters win over
 *  position ("26.7E 58.4N" is lon-first), then range-check. */
function orient(a: Token, b: Token): LatLon | null {
  const signed = (t: Token) =>
    t.hemi === 'S' || t.hemi === 'W' ? -Math.abs(t.value) : t.value;
  const isLat = (t: Token) => t.hemi === 'N' || t.hemi === 'S';
  const isLon = (t: Token) => t.hemi === 'E' || t.hemi === 'W';

  const swapped = isLon(a) || isLat(b);
  const lat = signed(swapped ? b : a);
  const lon = signed(swapped ? a : b);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

function parseDms(s: string): LatLon | null {
  const parts: Token[] = [];
  for (const m of s.matchAll(DMS_RE)) {
    if (m[2] === undefined) continue;
    const deg =
      Number(m[2]) + Number(m[3]) / 60 + (m[4] !== undefined ? Number(m[4]) / 3600 : 0);
    const hemi = (m[1] ?? m[5])?.toUpperCase() ?? null;
    parts.push({ value: deg, hemi });
  }
  return parts.length === 2 ? orient(parts[0], parts[1]) : null;
}

export function parseLatLon(raw: string): LatLon | null {
  let s = (raw ?? '').trim();
  if (s === '') return null;

  // DMS first: degree marks + minute marks means the plain tokenizer
  // would read degrees and minutes as a lat/lon pair — parse properly.
  // (Decimal degrees with a bare ° — "58.38° N" — have no minute mark
  // and stay on the tokenizer path.)
  if (/°/.test(s) && /\d\s*['′’]/.test(s)) return parseDms(s);

  // OSM URLs: "#map=<zoom>/<lat>/<lon>" — zoom comes FIRST.
  const osm = s.match(OSM_RE);
  if (osm) return orient({ value: Number(osm[1]), hemi: null }, { value: Number(osm[2]), hemi: null });

  // Google Maps URLs carry the viewport centre after '@' with a
  // trailing zoom ("…@58.38,26.73,14z"). Extras after the pair are
  // expected THERE, so the URL path scans and takes the first two.
  const at = s.indexOf('@');
  if (at !== -1) {
    const tokens: Token[] = [];
    for (const m of s.slice(at + 1).matchAll(TOKEN_RE)) {
      if (m[2] === undefined) continue;
      tokens.push({ value: Number(m[2]), hemi: (m[1] ?? m[3])?.toUpperCase() ?? null });
      if (tokens.length === 2) break; // zoom etc.
    }
    return tokens.length === 2 ? orient(tokens[0], tokens[1]) : null;
  }

  // Plain text must BE a pair, wholly — leftover characters or extra
  // numbers ("58,4 26,7", "58.4, 26.7, 14", "v1.2.3") mean this isn't
  // a coordinate paste; show the inline error instead of guessing.
  const m = s.match(PAIR_RE);
  if (!m) return null;
  return orient(
    { value: Number(m[2]), hemi: (m[1] ?? m[3])?.toUpperCase() ?? null },
    { value: Number(m[5]), hemi: (m[4] ?? m[6])?.toUpperCase() ?? null },
  );
}

/** Committed gate → input text. Empty when either half is unset. */
export function formatLatLon(lat: number | null, lon: number | null): string {
  return lat == null || lon == null ? '' : `${lat}, ${lon}`;
}
